import { ASSET_REF_RE, DEFINE_CLOSE_RE, FENCE_OPEN_RE, FOR_CLOSE_RE, FOR_HEADER_RE, REF_STRUCTURED_RE, UNIT_IDENTITY_REF_RE, getMarkdownCodeSpanRanges, isEscaped, isInsideMarkdownCodeSpan, isMarkdownFenceClose, parseAttrs, parseMarkdownFenceOpen } from "./patterns.js";
//#region src/template.ts
/** Shared helpers for templateable Magic MD units. */
const INPUT_REF_RE = /\$ref\{input\/(\w[\w-]*)\}/g;
const INPUT_REF_TOKEN_RE = /(["'])\$ref\{input\/(\w[\w-]*)\}\1|\$ref\{input\/(\w[\w-]*)\}/g;
function extractTemplateParams(value) {
	const params = /* @__PURE__ */ new Set();
	collectTemplateParams(value, params, /* @__PURE__ */ new WeakSet());
	return Array.from(params);
}
function collectTemplateParams(value, params, visited) {
	if (typeof value === "string") {
		const re = new RegExp(INPUT_REF_RE.source, "g");
		let match;
		while ((match = re.exec(value)) !== null) params.add(match[1]);
		return;
	}
	if (!value || typeof value !== "object" || visited.has(value)) return;
	visited.add(value);
	if (Array.isArray(value)) {
		for (const child of value) collectTemplateParams(child, params, visited);
		return;
	}
	for (const child of Object.values(value)) collectTemplateParams(child, params, visited);
}
function hasTemplateInputRefs(value) {
	return extractTemplateParams(value).length > 0;
}
function applyTemplateInputRefsToValue(value, params) {
	if (typeof value === "string") return applyTemplateInputRefsToString(value, params);
	if (Array.isArray(value)) return value.map((item) => applyTemplateInputRefsToValue(item, params));
	if (value && typeof value === "object") {
		const result = {};
		for (const [key, child] of Object.entries(value)) result[key] = applyTemplateInputRefsToValue(child, params);
		return result;
	}
	return value;
}
function substituteTemplateInputRefs(body, params) {
	if (!params || Object.keys(params).length === 0) return body;
	const resolved = applyTemplateInputRefsToContent(body, params);
	return typeof resolved === "string" ? resolved : stringifyTemplateReplacement(resolved);
}
function applyTemplateInputRefsToString(value, params) {
	const full = value.match(/^\$ref\{input\/(\w[\w-]*)\}$/);
	if (full) return Object.prototype.hasOwnProperty.call(params, full[1]) ? params[full[1]] : value;
	return value.replace(INPUT_REF_TOKEN_RE, (match, _quote, quotedKey, bareKey) => {
		const key = quotedKey ?? bareKey;
		if (!key || !Object.prototype.hasOwnProperty.call(params, key)) return match;
		const resolved = params[key];
		return quotedKey ? stringifyJsonTemplateReplacement(resolved) : stringifyTemplateReplacement(resolved);
	});
}
function stringifyTemplateValue(value) {
	if (typeof value === "string") return value;
	if (value === void 0 || value === null) return "";
	try {
		return JSON.stringify(value) ?? "";
	} catch {
		return String(value);
	}
}
function stringifyTemplateReplacement(value) {
	if (value === void 0 || value === null) return "";
	if (typeof value === "string") return value;
	return stringifyTemplateValue(value);
}
function stringifyJsonTemplateReplacement(value) {
	try {
		return JSON.stringify(value ?? "") ?? "\"\"";
	} catch {
		return JSON.stringify(String(value ?? ""));
	}
}
function applyTemplateInputRefsToContent(body, params) {
	const source = splitSourceLines(body);
	source.lines = rewriteTemplateInputLines(source.lines, params, /* @__PURE__ */ new Set());
	return joinSourceLines(source);
}
function rewriteTemplateInputLines(lines, params, shadowedInputs, allowConfigFenceRefs = false) {
	const result = [];
	const normalized = lines.join("\n");
	const lineStarts = [0];
	for (let line = 0; line < lines.length - 1; line++) lineStarts.push(lineStarts[line] + lines[line].length + 1);
	const codeSpanRanges = getMarkdownCodeSpanRanges(normalized);
	const replaceLine = (value, line, currentShadowed, raw = false) => replaceUnshadowedInputRefs(value, params, currentShadowed, raw ? void 0 : (offset) => isEscaped(value, offset) || isInsideMarkdownCodeSpan(codeSpanRanges, (lineStarts[line] ?? 0) + offset));
	let i = 0;
	let markdownFence = null;
	let liveConfigFence = null;
	while (i < lines.length) {
		const trimmed = lines[i].trim();
		if (liveConfigFence) {
			if (isMarkdownFenceClose(trimmed, liveConfigFence)) {
				liveConfigFence = null;
				result.push(lines[i]);
			} else result.push(replaceLine(lines[i], i, shadowedInputs, true));
			i++;
			continue;
		}
		if (markdownFence) {
			result.push(lines[i]);
			if (isMarkdownFenceClose(trimmed, markdownFence)) markdownFence = null;
			i++;
			continue;
		}
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		if (fenceOpen) {
			if (allowConfigFenceRefs && fenceOpen.char === "`" && fenceOpen.info === "config") {
				liveConfigFence = fenceOpen;
				result.push(lines[i]);
				i++;
				continue;
			}
			markdownFence = fenceOpen;
			result.push(lines[i]);
			i++;
			continue;
		}
		const block = findTemplateableBlock(lines, i);
		if (!block) {
			result.push(replaceLine(lines[i], i, shadowedInputs));
			i++;
			continue;
		}
		const blockLines = lines.slice(i, block.end + 1);
		const blockKind = parseTemplateableHeaderKind(blockLines[0].trim());
		const localInputs = collectTopLevelInputs(blockLines);
		const nextShadowed = /* @__PURE__ */ new Set([...shadowedInputs, ...localInputs]);
		result.push(replaceLine(blockLines[0], i, nextShadowed));
		if (blockLines.length > 2) result.push(...rewriteTemplateInputLines(blockLines.slice(1, -1), params, nextShadowed, blockKind === "define" || blockKind === "use"));
		if (blockLines.length > 1) result.push(replaceLine(blockLines[blockLines.length - 1], block.end, nextShadowed));
		i = block.end + 1;
	}
	return result;
}
function replaceUnshadowedInputRefs(value, params, shadowedInputs, isProtected) {
	return replaceLiveInputRefs(value, (match, _quote, quotedKey, bareKey) => {
		const key = quotedKey ?? bareKey;
		if (!key || shadowedInputs.has(key) || !Object.prototype.hasOwnProperty.call(params, key)) return match;
		const resolved = params[key];
		return quotedKey ? stringifyJsonTemplateReplacement(resolved) : stringifyTemplateReplacement(resolved);
	}, isProtected);
}
function findTemplateableBlock(lines, start) {
	const kind = parseTemplateableHeaderKind(lines[start]?.trim() ?? "");
	if (!kind) return null;
	if (kind === "for") {
		let depth = 1;
		let i = start + 1;
		let markdownFence = null;
		while (i < lines.length) {
			const current = lines[i].trim();
			if (markdownFence) {
				if (isMarkdownFenceClose(current, markdownFence)) markdownFence = null;
				i++;
				continue;
			}
			const fenceOpen = parseMarkdownFenceOpen(current);
			if (fenceOpen) {
				markdownFence = fenceOpen;
				i++;
				continue;
			}
			if (/^::for\[[^\]]+\]\{.*\}\s*$/.test(current)) depth++;
			if (/^::endfor\s*$/.test(current)) {
				depth--;
				if (depth === 0) return { end: i };
			}
			i++;
		}
		return { end: lines.length - 1 };
	}
	let nested = 0;
	let forDepth = 0;
	let i = start + 1;
	let markdownFence = null;
	while (i < lines.length) {
		const current = lines[i].trim();
		if (markdownFence) {
			if (isMarkdownFenceClose(current, markdownFence)) markdownFence = null;
			i++;
			continue;
		}
		const fenceOpen = parseMarkdownFenceOpen(current);
		if (fenceOpen) {
			markdownFence = fenceOpen;
			i++;
			continue;
		}
		const currentKind = parseTemplateableHeaderKind(current);
		if (currentKind === "for") forDepth++;
		else if (currentKind) nested++;
		else if (/^::endfor\s*$/.test(current)) {
			if (forDepth > 0) forDepth--;
		} else if (/^::\s*$/.test(current)) {
			if (nested > 0) nested--;
			else if (forDepth === 0) return { end: i };
		}
		i++;
	}
	return { end: lines.length - 1 };
}
function parseTemplateableHeaderKind(line) {
	if (/^::define\[[^\]]+\]\{.*\}\s*$/.test(line)) return "define";
	if (/^::content\[[^\]]+\](?:\{.*\})?\s*$/.test(line)) return "content";
	if (/^::for\[[^\]]+\]\{.*\}\s*$/.test(line)) return "for";
	if (/^::use\[[^\]]+\]\{.*\}\s*$/.test(line)) return "use";
	return null;
}
function collectTopLevelInputs(blockLines) {
	const inputs = /* @__PURE__ */ new Set();
	let i = 1;
	let nested = 0;
	let forDepth = 0;
	let markdownFence = null;
	while (i < blockLines.length) {
		const trimmed = blockLines[i].trim();
		if (markdownFence) {
			if (isMarkdownFenceClose(trimmed, markdownFence)) markdownFence = null;
			i++;
			continue;
		}
		const kind = parseTemplateableHeaderKind(trimmed);
		if (kind === "for") {
			forDepth++;
			i++;
			continue;
		}
		if (kind) {
			nested++;
			i++;
			continue;
		}
		if (/^::endfor\s*$/.test(trimmed)) {
			if (forDepth > 0) forDepth--;
			i++;
			continue;
		}
		if (/^::\s*$/.test(trimmed)) {
			if (nested > 0) nested--;
			i++;
			continue;
		}
		if (nested === 0 && forDepth === 0) {
			if (/^`{3,}inputs\s*$/.exec(trimmed)) {
				const jsonLines = [];
				i++;
				while (i < blockLines.length && !/^`{3,}\s*$/.test(blockLines[i].trim())) {
					jsonLines.push(blockLines[i]);
					i++;
				}
				try {
					const parsed = JSON.parse(jsonLines.join("\n"));
					for (const key of Object.keys(parsed.properties ?? {})) if (/^\w[\w-]*$/.test(key)) inputs.add(key);
				} catch {}
			} else {
				const fenceOpen = parseMarkdownFenceOpen(trimmed);
				if (fenceOpen) markdownFence = fenceOpen;
			}
		} else {
			const fenceOpen = parseMarkdownFenceOpen(trimmed);
			if (fenceOpen) markdownFence = fenceOpen;
		}
		i++;
	}
	return inputs;
}
function replaceLiveInputRefs(value, replacement, isProtected) {
	const re = new RegExp(INPUT_REF_TOKEN_RE.source, "g");
	return value.replace(re, (match, quote, quotedKey, bareKey, offset) => {
		if (isProtected?.(offset)) return match;
		return replacement(match, quote, quotedKey, bareKey);
	});
}
function splitSourceLines(content) {
	const lines = [];
	const separators = [];
	let start = 0;
	const re = /\r\n|\n/g;
	let match;
	while ((match = re.exec(content)) !== null) {
		lines.push(content.slice(start, match.index));
		separators.push(match[0]);
		start = match.index + match[0].length;
	}
	lines.push(content.slice(start));
	return {
		lines,
		separators
	};
}
function joinSourceLines(source) {
	return source.lines.map((line, index) => line + (source.separators[index] ?? "")).join("");
}
//#endregion
//#region src/template-inputs.ts
const TEMPLATE_INPUTS_FENCE_RE = /^(`{3,})inputs\s*$/;
function parseTemplateInputSchemaJson(json, line, ownerLabel) {
	const diagnostics = [];
	const trimmed = json?.trim() ?? "";
	if (!trimmed) return { diagnostics };
	try {
		return {
			schema: normalizeTemplateInputSchema(JSON.parse(trimmed)),
			diagnostics
		};
	} catch (error) {
		diagnostics.push({
			line,
			severity: "warning",
			message: `${ownerLabel} inputs JSON parse failed: ${error instanceof Error ? error.message : String(error)}`,
			code: "template-inputs-invalid-json"
		});
		return { diagnostics };
	}
}
function normalizeTemplateInputSchema(value) {
	if (!value || typeof value !== "object" || Array.isArray(value)) return {
		type: "object",
		properties: {}
	};
	const source = value;
	const properties = {};
	const rawProperties = source.properties;
	if (rawProperties && typeof rawProperties === "object" && !Array.isArray(rawProperties)) for (const [name, rawProp] of Object.entries(rawProperties)) {
		if (!isTemplateInputName(name)) continue;
		properties[name] = normalizeTemplateInputProperty(rawProp);
	}
	const required = Array.isArray(source.required) ? source.required.filter((item) => typeof item === "string" && isTemplateInputName(item)) : [];
	const order = Array.isArray(source.order) ? source.order.filter((item) => typeof item === "string" && isTemplateInputName(item)) : void 0;
	return {
		type: "object",
		...required.length > 0 ? { required: uniqueStrings(required) } : {},
		...order && order.length > 0 ? { order: uniqueStrings(order) } : {},
		properties
	};
}
function getTemplateInputSchemaParams(schema) {
	if (!schema?.properties) return [];
	const keys = Object.keys(schema.properties);
	if (!schema.order?.length) return keys;
	const ordered = schema.order.filter((key) => keys.includes(key));
	return [...ordered, ...keys.filter((key) => !ordered.includes(key))];
}
function mergeTemplateParams(usedParams, schema) {
	return uniqueStrings([...getTemplateInputSchemaParams(schema), ...usedParams]);
}
function buildDefaultedTemplateInputs(values, schema) {
	const result = { ...values ?? {} };
	for (const [name, prop] of Object.entries(schema?.properties ?? {})) {
		if (Object.prototype.hasOwnProperty.call(result, name)) continue;
		if (Object.prototype.hasOwnProperty.call(prop, "default")) result[name] = structuredClone(prop.default);
	}
	return result;
}
function getRequiredTemplateInputs(schema, fallbackParams) {
	if (schema) return [...schema.required ?? []];
	return [...fallbackParams ?? []];
}
function getBlockingTemplateParams(schema, fallbackParams, unresolvedParams) {
	const params = uniqueStrings([...unresolvedParams]);
	if (!schema) return params;
	const required = new Set(getRequiredTemplateInputs(schema, fallbackParams));
	const declared = new Set(Object.keys(schema.properties ?? {}));
	return params.filter((param) => required.has(param) || !declared.has(param));
}
function validateTemplateInputValues(values, schema, line) {
	if (!schema?.properties) return [];
	const diagnostics = [];
	const withDefaults = buildDefaultedTemplateInputs(values, schema);
	for (const name of schema.required ?? []) {
		const value = withDefaults[name];
		if (value === void 0 || value === null || value === "") diagnostics.push({
			line,
			severity: "warning",
			message: `Template parameter "${name}" not provided in ::use`,
			code: "use-missing-param"
		});
	}
	for (const [name, value] of Object.entries(withDefaults)) {
		const prop = schema.properties[name];
		if (!prop?.type || value === void 0 || value === null || value === "") continue;
		if (isTemplateReferenceValue(value)) continue;
		if (!matchesTemplateInputType(value, prop.type)) diagnostics.push({
			line,
			severity: "warning",
			message: `Template parameter "${name}" expects ${prop.type}, got ${Array.isArray(value) ? "array" : typeof value}`,
			code: "use-param-type-mismatch"
		});
	}
	return diagnostics;
}
function inferTemplateInputSchema(params) {
	if (params.length === 0) return void 0;
	return {
		type: "object",
		order: [...params],
		properties: Object.fromEntries(params.map((param) => [param, { type: "string" }]))
	};
}
function normalizeTemplateInputProperty(value) {
	if (!value || typeof value !== "object" || Array.isArray(value)) return { type: "string" };
	const source = value;
	const type = normalizeTemplateInputType(source.type);
	const prop = { ...type ? { type } : {} };
	if (typeof source.title === "string") prop.title = source.title;
	if (typeof source.description === "string") prop.description = source.description;
	if (Array.isArray(source.enum)) prop.enum = source.enum;
	if (Object.prototype.hasOwnProperty.call(source, "default")) prop.default = source.default;
	return prop;
}
function normalizeTemplateInputType(value) {
	return value === "string" || value === "number" || value === "integer" || value === "boolean" || value === "array" || value === "object" ? value : void 0;
}
function isTemplateReferenceValue(value) {
	return typeof value === "string" && /^\$(?:ref|var)\{[^}]+\}$/.test(value.trim());
}
function matchesTemplateInputType(value, type) {
	if (type === "array") return Array.isArray(value);
	if (type === "integer") return typeof value === "number" && Number.isInteger(value);
	if (type === "number") return typeof value === "number";
	if (type === "object") return Boolean(value) && typeof value === "object" && !Array.isArray(value);
	return typeof value === type;
}
function isTemplateInputName(value) {
	return /^\w[\w-]*$/.test(value);
}
function uniqueStrings(values) {
	return [...new Set(values)];
}
//#endregion
//#region src/assets.ts
const WINDOWS_DRIVE_RE = /^[a-zA-Z]:\//;
function normalizeSlashPath(path) {
	return path.replace(/\\/g, "/");
}
const ASSETS_REF_PREFIX = "assets/";
function normalizeAssetRefPath(rawPath) {
	const normalized = normalizeSlashPath(rawPath).trim();
	if (!normalized) return null;
	if (normalized.startsWith("/") || WINDOWS_DRIVE_RE.test(normalized)) return null;
	const segments = normalized.split("/");
	if (segments.some((segment) => segment === "" || segment === "." || segment === "..")) return null;
	return segments.join("/");
}
function parseAssetRefPath(refPath) {
	if (refPath.startsWith("assets/")) {
		const relativePath = normalizeAssetRefPath(refPath.slice(7));
		return relativePath ? { relativePath } : null;
	}
	const namespaceMatch = refPath.match(/^([\w-]+)\/assets\/(.+)$/);
	if (!namespaceMatch) return null;
	const relativePath = normalizeAssetRefPath(namespaceMatch[2]);
	return relativePath ? {
		namespace: namespaceMatch[1],
		relativePath
	} : null;
}
function isAssetRefPath(refPath) {
	return parseAssetRefPath(refPath) !== null;
}
function getPathDirname(filePath) {
	const normalized = normalizeSlashPath(filePath);
	const lastSlash = normalized.lastIndexOf("/");
	if (lastSlash < 0) return "";
	return lastSlash === 0 ? "/" : normalized.slice(0, lastSlash);
}
function getDocumentBundleDir(filePath) {
	const normalized = normalizeSlashPath(filePath);
	const dir = getPathDirname(normalized);
	const base = normalized.slice(dir === "/" ? 1 : dir.length + 1);
	const dot = base.lastIndexOf(".");
	const stem = dot > 0 ? base.slice(0, dot) : base;
	return dir ? `${dir}/${stem}.magic-md` : `${stem}.magic-md`;
}
function getDocumentAssetsDir(filePath) {
	return `${getDocumentBundleDir(filePath)}/assets`;
}
function resolveDocumentAssetPath(documentPath, rawAssetPath) {
	const relativePath = normalizeAssetRefPath(rawAssetPath);
	return relativePath ? `${getDocumentAssetsDir(documentPath)}/${relativePath}` : null;
}
//#endregion
//#region src/scanner/index.ts
const DEFINE_OPEN_RE = /^::define\[([^\]]+)\]\{([^}]+)\}\s*$/;
const RENDER_RE = /::render\[([^\]]+)\](?:\{[^}]*\})?::/g;
const REF_RE = REF_STRUCTURED_RE;
const META_OPEN_RE = /^::meta\[(\w+)\]\s*$/;
const IMPORT_LINE_RE = /^::import\{(.+)\}\s*$/;
const USE_OPEN_RE = /^::use\[(\w[\w-]*)\]\{([^}]+)\}\s*$/;
const CONTENT_OPEN_RE = /^::content\[(\w[\w-]*)\](\{[^}]*\})?\s*$/;
/** Regex matching block openers that close with a standalone `::` line — for nesting depth tracking */
const BLOCK_OPENER_RE = /^::(define\[|content\[|meta\[|use\[)/;
const IMPORT_OPENER_RE = /^::import\{/;
function isBlockFormImport(lines, headerIndex) {
	let cursor = headerIndex + 1;
	while (cursor < lines.length && lines[cursor].trim() === "") cursor++;
	if (cursor >= lines.length) return false;
	const next = lines[cursor].trim();
	if (DEFINE_CLOSE_RE.test(next)) return true;
	return FENCE_OPEN_RE.exec(next)?.[2] === "output";
}
/** Parse ::for attrs string for `each` and `of` values */
function parseForAttrs(attrsStr) {
	const attrs = parseAttrs(attrsStr);
	return {
		each: attrs.each ?? "",
		of: attrs.of ?? "",
		template: attrs.template === "true"
	};
}
function tryParseInputsSchema(lines) {
	const text = lines.join("\n").trim();
	if (!text) return void 0;
	try {
		return normalizeTemplateInputSchema(JSON.parse(text));
	} catch {
		return;
	}
}
/**
* Scan raw text lines for Magic MD syntax elements.
* Returns positional information for all ::define, ::render, $ref{},
* and module directives (::meta, ::import, ::use, ::content).
*
* Renamed from `scanLines` to avoid conflict with core parser's internal `scanLines()`.
*/
function scanDocument(lines) {
	const defines = [];
	const renders = [];
	const refs = [];
	const assetRefs = [];
	const unitRefs = [];
	const metaBlocks = [];
	const imports = [];
	const useBlocks = [];
	const contentDefines = [];
	const forBlocks = [];
	const mdFencedLines = /* @__PURE__ */ new Set();
	const mdFencedBlocks = [];
	const documentText = lines.join("\n");
	const documentLineStarts = [0];
	for (let line = 0; line < lines.length - 1; line++) documentLineStarts.push(documentLineStarts[line] + lines[line].length + 1);
	const codeSpanRanges = getMarkdownCodeSpanRanges(documentText);
	const isLiveMatch = (line, index, lineNumber) => !isEscaped(line, index) && !isInsideMarkdownCodeSpan(codeSpanRanges, (documentLineStarts[lineNumber] ?? 0) + index);
	const lineCount = lines.length;
	let i = 0;
	while (i < lineCount) {
		const lineText = lines[i];
		const syntaxLine = lineText.trim();
		const mdFenceOpen = parseMarkdownFenceOpen(syntaxLine);
		if (mdFenceOpen) {
			const fenceStart = i;
			let k = i + 1;
			while (k < lineCount) {
				if (isMarkdownFenceClose(lines[k].trim(), mdFenceOpen)) {
					k++;
					break;
				}
				k++;
			}
			const fenceEnd = k - 1;
			for (let f = fenceStart; f <= fenceEnd; f++) mdFencedLines.add(f);
			mdFencedBlocks.push({
				startLine: fenceStart,
				endLine: fenceEnd,
				marker: mdFenceOpen.char,
				info: mdFenceOpen.info
			});
			i = k;
			continue;
		}
		const metaMatch = META_OPEN_RE.exec(syntaxLine);
		if (metaMatch) {
			const domain = metaMatch[1];
			const startLine = i;
			let endLine = lineCount - 1;
			let config;
			let configRange;
			let j = i + 1;
			let inConfig = false;
			let configStart = -1;
			let activeFenceLen = 3;
			const configLines = [];
			while (j < lineCount) {
				const inner = lines[j];
				if (DEFINE_CLOSE_RE.test(inner.trim()) && !inConfig) {
					endLine = j;
					break;
				}
				const fenceMatch = FENCE_OPEN_RE.exec(inner.trim());
				if (!inConfig && fenceMatch && fenceMatch[2] === "config") {
					activeFenceLen = fenceMatch[1].length;
					inConfig = true;
					configStart = j + 1;
				} else if (inConfig && inner.trim().length === activeFenceLen && inner.trim() === "`".repeat(activeFenceLen)) {
					inConfig = false;
					configRange = {
						start: configStart,
						end: j - 1
					};
					config = configLines.join("\n");
					activeFenceLen = 3;
				} else if (inConfig) configLines.push(inner);
				j++;
			}
			metaBlocks.push({
				domain,
				startLine,
				endLine,
				config,
				configRange
			});
			i = endLine + 1;
			continue;
		}
		const importMatch = IMPORT_LINE_RE.exec(syntaxLine);
		if (importMatch) {
			const attrsStr = importMatch[1];
			const fromM = attrsStr.match(/from="([^"]+)"/);
			const nsM = attrsStr.match(/ns="([^"]+)"/);
			const pickM = attrsStr.match(/pick="([^"]+)"/);
			const excludeM = attrsStr.match(/exclude="([^"]+)"/);
			const renameM = attrsStr.match(/rename="([^"]+)"/);
			const outputM = attrsStr.match(/\boutput="([^"]+)"/);
			const documentM = attrsStr.match(/\bdocument="([^"]+)"/);
			const rawOutputAttr = outputM?.[1];
			const importOutputMode = rawOutputAttr === "external" || rawOutputAttr === "inline" ? rawOutputAttr : void 0;
			const source = fromM?.[1] ?? "";
			const namespace = nsM?.[1] ?? (source ? deriveNamespace(source) : "");
			const pick = pickM ? pickM[1].split(",").map((s) => s.trim()) : void 0;
			const exclude = excludeM ? excludeM[1].split(",").map((s) => s.trim()) : void 0;
			let rename;
			if (renameM) {
				rename = {};
				for (const pair of renameM[1].split(",")) {
					const [from, to] = pair.split(":").map((s) => s.trim());
					if (from && to) rename[from] = to;
				}
			}
			let endLine = i;
			let outputRange;
			let nextI = i + 1;
			while (nextI < lineCount && lines[nextI].trim() === "") nextI++;
			if (nextI < lineCount) {
				if (DEFINE_CLOSE_RE.test(lines[nextI].trim())) endLine = nextI;
				else {
					const fenceM = FENCE_OPEN_RE.exec(lines[nextI].trim());
					if (fenceM && fenceM[2] === "output") {
						const fLen = fenceM[1].length;
						let j = nextI + 1;
						while (j < lineCount) {
							const jTrimmed = lines[j].trim();
							if (jTrimmed.length === fLen && jTrimmed === "`".repeat(fLen)) {
								outputRange = {
									start: nextI + 1,
									end: j - 1
								};
								let closeI = j + 1;
								while (closeI < lineCount && lines[closeI].trim() === "") closeI++;
								if (closeI < lineCount && DEFINE_CLOSE_RE.test(lines[closeI].trim())) endLine = closeI;
								else endLine = j;
								break;
							}
							j++;
						}
						if (j >= lineCount) {}
					}
				}
			}
			imports.push({
				namespace,
				source,
				pick,
				exclude,
				rename,
				document: documentM?.[1] === "true",
				line: i,
				endLine,
				startChar: lineText.length - lineText.trimStart().length,
				endChar: lineText.length,
				outputRange,
				outputMode: importOutputMode
			});
			i = endLine + 1;
			continue;
		}
		const forMatch = FOR_HEADER_RE.exec(syntaxLine);
		if (forMatch) {
			const id = forMatch[1];
			const attrsStr = forMatch[2];
			const { each, of, template } = parseForAttrs(attrsStr);
			const startLine = i;
			let endLine = lineCount - 1;
			let outputRange;
			let inputsRange;
			let inputSchema;
			let nestedForDepth = 0;
			const topLevelOutputFences = [];
			const topLevelIgnoredFences = [];
			let j = i + 1;
			while (j < lineCount) {
				const jTrimmed = lines[j].trim();
				if (nestedForDepth === 0) {
					const inputsMatch = TEMPLATE_INPUTS_FENCE_RE.exec(jTrimmed);
					if (inputsMatch) {
						const fenceLen = inputsMatch[1].length;
						const inputLines = [];
						let closed = false;
						let k = j + 1;
						while (k < lineCount) {
							const kTrimmed = lines[k].trim();
							if (kTrimmed.length === fenceLen && kTrimmed === "`".repeat(fenceLen)) {
								inputsRange = {
									start: j + 1,
									end: k - 1
								};
								inputSchema = tryParseInputsSchema(inputLines);
								topLevelIgnoredFences.push({
									start: j,
									end: k
								});
								j = k + 1;
								closed = true;
								break;
							}
							inputLines.push(lines[k]);
							k++;
						}
						if (closed) continue;
					}
					const fenceMatch = FENCE_OPEN_RE.exec(jTrimmed);
					if (fenceMatch && fenceMatch[2] === "output") {
						const fenceLen = fenceMatch[1].length;
						let closed = false;
						let k = j + 1;
						while (k < lineCount) {
							const kTrimmed = lines[k].trim();
							if (kTrimmed.length === fenceLen && kTrimmed === "`".repeat(fenceLen)) {
								outputRange = {
									start: j + 1,
									end: k - 1
								};
								topLevelOutputFences.push({
									start: j,
									end: k
								});
								j = k + 1;
								closed = true;
								break;
							}
							k++;
						}
						if (closed) continue;
					}
				}
				const markdownFence = parseMarkdownFenceOpen(jTrimmed);
				if (markdownFence) {
					j++;
					while (j < lineCount) {
						if (isMarkdownFenceClose(lines[j].trim(), markdownFence)) {
							j++;
							break;
						}
						j++;
					}
					continue;
				}
				if (FOR_HEADER_RE.test(jTrimmed)) nestedForDepth++;
				else if (FOR_CLOSE_RE.test(jTrimmed)) {
					if (nestedForDepth > 0) nestedForDepth--;
					else {
						endLine = j;
						break;
					}
				}
				j++;
			}
			const params = mergeTemplateParams(extractTemplateParams(of), inputSchema);
			forBlocks.push({
				id,
				each,
				of,
				startLine,
				endLine,
				outputRange,
				inputsRange,
				inputSchema,
				...template ? { template: true } : {},
				...params.length > 0 ? { params } : {}
			});
			const innerStart = startLine + 1;
			if (endLine > innerStart) mergeInnerResults(scanDocument(maskLineRanges(lines.slice(innerStart, endLine), [...topLevelOutputFences, ...topLevelIgnoredFences], innerStart)), innerStart, defines, renders, refs, assetRefs, unitRefs, metaBlocks, imports, useBlocks, contentDefines, forBlocks, mdFencedLines, mdFencedBlocks);
			i = endLine + 1;
			continue;
		}
		const contentMatch = CONTENT_OPEN_RE.exec(syntaxLine);
		if (contentMatch) {
			const id = contentMatch[1];
			const attrsStr = contentMatch[2] ?? "";
			const contentAttrs = attrsStr ? parseAttrs(attrsStr) : {};
			const contentOutputMode = contentAttrs.output === "external" || contentAttrs.output === "inline" ? contentAttrs.output : void 0;
			const contentTemplate = contentAttrs.template === "true";
			const startLine = i;
			let endLine = lineCount - 1;
			const bodyLines = [];
			let nestingDepth = 0;
			let nestedForDepth = 0;
			let outputRange;
			let inputsRange;
			let inputSchema;
			const topLevelOutputFences = [];
			const topLevelIgnoredFences = [];
			let j = i + 1;
			while (j < lineCount) {
				const jLine = lines[j];
				const jTrimmed = jLine.trim();
				if (nestingDepth === 0 && nestedForDepth === 0) {
					const inputsMatch = TEMPLATE_INPUTS_FENCE_RE.exec(jTrimmed);
					const outputMatch = FENCE_OPEN_RE.exec(jTrimmed);
					if (inputsMatch || outputMatch && outputMatch[2] === "output") {
						const fenceLen = inputsMatch?.[1].length ?? outputMatch[1].length;
						const inputLines = [];
						let k = j + 1;
						while (k < lineCount) {
							if (isMarkdownFenceClose(lines[k].trim(), {
								char: "`",
								length: fenceLen,
								info: ""
							})) {
								if (inputsMatch) {
									inputsRange = {
										start: j + 1,
										end: k - 1
									};
									inputSchema = tryParseInputsSchema(inputLines);
									topLevelIgnoredFences.push({
										start: j,
										end: k
									});
								} else {
									outputRange = {
										start: j + 1,
										end: k - 1
									};
									topLevelOutputFences.push({
										start: j,
										end: k
									});
								}
								j = k + 1;
								break;
							}
							if (inputsMatch) inputLines.push(lines[k]);
							k++;
						}
						if (j === k + 1) continue;
					}
				}
				const markdownFence = parseMarkdownFenceOpen(jTrimmed);
				if (markdownFence) {
					bodyLines.push(jLine);
					j++;
					while (j < lineCount) {
						bodyLines.push(lines[j]);
						if (isMarkdownFenceClose(lines[j].trim(), markdownFence)) {
							j++;
							break;
						}
						j++;
					}
					continue;
				}
				if (BLOCK_OPENER_RE.test(jTrimmed)) {
					nestingDepth++;
					bodyLines.push(jLine);
				} else if (IMPORT_OPENER_RE.test(jTrimmed)) {
					if (isBlockFormImport(lines, j)) nestingDepth++;
					bodyLines.push(jLine);
				} else if (FOR_HEADER_RE.test(jTrimmed)) {
					nestedForDepth++;
					bodyLines.push(jLine);
				} else if (FOR_CLOSE_RE.test(jTrimmed)) {
					if (nestedForDepth > 0) nestedForDepth--;
					bodyLines.push(jLine);
				} else if (DEFINE_CLOSE_RE.test(jLine)) {
					if (nestingDepth > 0) {
						nestingDepth--;
						bodyLines.push(jLine);
					} else if (nestedForDepth > 0) bodyLines.push(jLine);
					else {
						endLine = j;
						break;
					}
				} else bodyLines.push(jLine);
				j++;
			}
			const body = bodyLines.join("\n");
			const params = mergeTemplateParams(extractTemplateParams(body), inputSchema);
			const isTemplate = contentTemplate;
			contentDefines.push({
				id,
				isTemplate,
				...contentTemplate ? { template: true } : {},
				...params.length > 0 ? { params } : {},
				startLine,
				endLine,
				body,
				outputRange,
				inputsRange,
				inputSchema,
				outputMode: contentOutputMode
			});
			const innerStart = startLine + 1;
			if (endLine > innerStart) mergeInnerResults(scanDocument(maskLineRanges(lines.slice(innerStart, endLine), [...topLevelOutputFences, ...topLevelIgnoredFences], innerStart)), innerStart, defines, renders, refs, assetRefs, unitRefs, metaBlocks, imports, useBlocks, contentDefines, forBlocks, mdFencedLines, mdFencedBlocks);
			i = endLine + 1;
			continue;
		}
		const useMatch = USE_OPEN_RE.exec(syntaxLine);
		if (useMatch) {
			const id = useMatch[1];
			const attrsStr = useMatch[2];
			const attrs = parseAttrs(attrsStr);
			const from = attrs.from ?? "";
			const rawOutputAttr = attrs.output;
			const useOutputMode = rawOutputAttr === "external" || rawOutputAttr === "inline" ? rawOutputAttr : void 0;
			const rawConfigAttr = attrs.config;
			const useConfigMode = rawConfigAttr === "external" || rawConfigAttr === "inline" ? rawConfigAttr : void 0;
			const useTemplate = attrs.template === "true";
			const startLine = i;
			let endLine = lineCount - 1;
			let config;
			let configRange;
			let inputsRange;
			let inputSchema;
			let j = i + 1;
			let inFence = false;
			let fenceKind = "";
			let configStart = -1;
			let inputsStart = -1;
			let outputStart = -1;
			let activeFenceLen = 3;
			const configLines = [];
			const inputsLines = [];
			let outputRange;
			while (j < lineCount) {
				const inner = lines[j];
				const innerTrimmed = inner.trim();
				if (DEFINE_CLOSE_RE.test(innerTrimmed) && !inFence) {
					endLine = j;
					break;
				}
				if (!inFence) {
					const inputsMatch = TEMPLATE_INPUTS_FENCE_RE.exec(innerTrimmed);
					if (inputsMatch) {
						activeFenceLen = inputsMatch[1].length;
						fenceKind = "inputs";
						inFence = true;
						inputsStart = j + 1;
						j++;
						continue;
					}
					const fenceMatch = FENCE_OPEN_RE.exec(innerTrimmed);
					if (fenceMatch) {
						activeFenceLen = fenceMatch[1].length;
						fenceKind = fenceMatch[2];
						inFence = true;
						if (fenceKind === "config") configStart = j + 1;
						else outputStart = j + 1;
					}
				} else if (innerTrimmed.length === activeFenceLen && innerTrimmed === "`".repeat(activeFenceLen)) {
					if (fenceKind === "config") {
						configRange = {
							start: configStart,
							end: j - 1
						};
						config = configLines.join("\n");
					} else if (fenceKind === "output") outputRange = {
						start: outputStart,
						end: j - 1
					};
					else if (fenceKind === "inputs") {
						inputsRange = {
							start: inputsStart,
							end: j - 1
						};
						inputSchema = tryParseInputsSchema(inputsLines);
					}
					inFence = false;
					fenceKind = "";
					activeFenceLen = 3;
				} else if (fenceKind === "config") configLines.push(inner);
				else if (fenceKind === "inputs") inputsLines.push(inner);
				j++;
			}
			const params = mergeTemplateParams(extractTemplateParams(config), inputSchema);
			useBlocks.push({
				id,
				from,
				startLine,
				endLine,
				config,
				configRange,
				inputsRange,
				inputSchema,
				outputRange,
				outputMode: useOutputMode,
				configMode: useConfigMode,
				...useTemplate ? { template: true } : {},
				...params.length > 0 ? { params } : {}
			});
			i = endLine + 1;
			continue;
		}
		const defineMatch = DEFINE_OPEN_RE.exec(syntaxLine);
		if (defineMatch) {
			const id = defineMatch[1];
			const attrs = parseAttrs(defineMatch[2]);
			const pluginName = attrs.plugin ?? "";
			const rawOutputAttr = attrs.output;
			const outputMode = rawOutputAttr === "external" || rawOutputAttr === "inline" ? rawOutputAttr : void 0;
			const rawConfigAttr = attrs.config;
			const configMode = rawConfigAttr === "external" || rawConfigAttr === "inline" ? rawConfigAttr : void 0;
			const template = attrs.template === "true";
			const startLine = i;
			let endLine = lineCount - 1;
			let config;
			let configRange;
			let inputsRange;
			let inputSchema;
			let j = i + 1;
			let inConfig = false;
			let inInputs = false;
			let inOutput = false;
			let configStart = -1;
			let inputsStart = -1;
			let outputStart = -1;
			let activeFenceLen = 3;
			const configLines = [];
			const inputsLines = [];
			let outputRange;
			while (j < lineCount) {
				const inner = lines[j];
				if (DEFINE_CLOSE_RE.test(inner.trim()) && !inConfig && !inInputs && !inOutput) {
					endLine = j;
					break;
				}
				const inputsMatch = TEMPLATE_INPUTS_FENCE_RE.exec(inner.trim());
				const fenceMatch = FENCE_OPEN_RE.exec(inner.trim());
				if (!inConfig && !inInputs && !inOutput && (inputsMatch || fenceMatch)) {
					activeFenceLen = inputsMatch?.[1].length ?? fenceMatch[1].length;
					if (inputsMatch) {
						inInputs = true;
						inputsStart = j + 1;
					} else if (fenceMatch[2] === "config") {
						inConfig = true;
						configStart = j + 1;
					} else {
						inOutput = true;
						outputStart = j + 1;
					}
				} else if ((inConfig || inInputs || inOutput) && inner.trim().length === activeFenceLen && inner.trim() === "`".repeat(activeFenceLen)) {
					if (inConfig) {
						inConfig = false;
						configRange = {
							start: configStart,
							end: j - 1
						};
						config = configLines.join("\n");
					} else if (inInputs) {
						inInputs = false;
						inputsRange = {
							start: inputsStart,
							end: j - 1
						};
						inputSchema = tryParseInputsSchema(inputsLines);
					} else {
						inOutput = false;
						outputRange = {
							start: outputStart,
							end: j - 1
						};
					}
					activeFenceLen = 3;
				} else if (inConfig) configLines.push(inner);
				else if (inInputs) inputsLines.push(inner);
				j++;
			}
			defines.push({
				id,
				pluginName,
				startLine,
				endLine,
				config,
				configRange,
				inputsRange,
				inputSchema,
				outputRange,
				outputMode,
				configMode,
				...template ? { template: true } : {},
				...mergeTemplateParams(extractTemplateParams(config), inputSchema).length > 0 ? { params: mergeTemplateParams(extractTemplateParams(config), inputSchema) } : {}
			});
			i = endLine + 1;
			continue;
		}
		let renderMatch;
		RENDER_RE.lastIndex = 0;
		renderMatch = RENDER_RE.exec(lineText);
		while (renderMatch !== null) {
			if (!isLiveMatch(lineText, renderMatch.index, i)) {
				renderMatch = RENDER_RE.exec(lineText);
				continue;
			}
			renders.push({
				definitionId: renderMatch[1],
				line: i,
				startChar: renderMatch.index,
				endChar: renderMatch.index + renderMatch[0].length
			});
			renderMatch = RENDER_RE.exec(lineText);
		}
		let refMatch;
		REF_RE.lastIndex = 0;
		refMatch = REF_RE.exec(lineText);
		while (refMatch !== null) {
			if (!isLiveMatch(lineText, refMatch.index, i)) {
				refMatch = REF_RE.exec(lineText);
				continue;
			}
			refs.push({
				full: refMatch[0],
				targetId: refMatch[1],
				accessor: refMatch[2] ?? "output",
				path: refMatch[3] ?? "",
				line: i,
				startChar: refMatch.index,
				endChar: refMatch.index + refMatch[0].length
			});
			refMatch = REF_RE.exec(lineText);
		}
		let assetRefMatch;
		ASSET_REF_RE.lastIndex = 0;
		assetRefMatch = ASSET_REF_RE.exec(lineText);
		while (assetRefMatch !== null) {
			if (!isLiveMatch(lineText, assetRefMatch.index, i)) {
				assetRefMatch = ASSET_REF_RE.exec(lineText);
				continue;
			}
			const parsedAssetRef = parseAssetRefPath(assetRefMatch[1]);
			const rawPath = assetRefMatch[1].replace(/^(?:[\w-]+\/)?assets\//, "");
			assetRefs.push({
				full: assetRefMatch[0],
				...parsedAssetRef?.namespace ? { namespace: parsedAssetRef.namespace } : {},
				relativePath: parsedAssetRef?.relativePath ?? rawPath,
				line: i,
				startChar: assetRefMatch.index,
				endChar: assetRefMatch.index + assetRefMatch[0].length,
				valid: parsedAssetRef !== null
			});
			assetRefMatch = ASSET_REF_RE.exec(lineText);
		}
		let unitRefMatch;
		UNIT_IDENTITY_REF_RE.lastIndex = 0;
		unitRefMatch = UNIT_IDENTITY_REF_RE.exec(lineText);
		while (unitRefMatch !== null) {
			if (!isLiveMatch(lineText, unitRefMatch.index, i)) {
				unitRefMatch = UNIT_IDENTITY_REF_RE.exec(lineText);
				continue;
			}
			unitRefs.push({
				full: unitRefMatch[0],
				unitId: unitRefMatch[1],
				line: i,
				startChar: unitRefMatch.index,
				endChar: unitRefMatch.index + unitRefMatch[0].length
			});
			unitRefMatch = UNIT_IDENTITY_REF_RE.exec(lineText);
		}
		i++;
	}
	return {
		defines,
		renders,
		refs,
		assetRefs,
		unitRefs,
		metaBlocks,
		imports,
		useBlocks,
		contentDefines,
		forBlocks,
		mdFencedLines,
		mdFencedBlocks
	};
}
/**
* Merge recursively scanned inner results into parent arrays, adjusting line offsets.
*/
function mergeInnerResults(inner, off, defines, renders, refs, assetRefs, unitRefs, metaBlocks, imports, useBlocks, contentDefines, forBlocks, mdFencedLines, mdFencedBlocks) {
	for (const d of inner.defines) {
		d.startLine += off;
		d.endLine += off;
		if (d.configRange) {
			d.configRange.start += off;
			d.configRange.end += off;
		}
		if (d.inputsRange) {
			d.inputsRange.start += off;
			d.inputsRange.end += off;
		}
		if (d.outputRange) {
			d.outputRange.start += off;
			d.outputRange.end += off;
		}
		defines.push(d);
	}
	for (const r of inner.renders) {
		r.line += off;
		renders.push(r);
	}
	for (const rf of inner.refs) {
		rf.line += off;
		refs.push(rf);
	}
	for (const ar of inner.assetRefs) {
		ar.line += off;
		assetRefs.push(ar);
	}
	for (const ur of inner.unitRefs) {
		ur.line += off;
		unitRefs.push(ur);
	}
	for (const m of inner.metaBlocks) {
		m.startLine += off;
		m.endLine += off;
		if (m.configRange) {
			m.configRange.start += off;
			m.configRange.end += off;
		}
		metaBlocks.push(m);
	}
	for (const imp of inner.imports) {
		imp.line += off;
		imp.endLine += off;
		if (imp.outputRange) {
			imp.outputRange.start += off;
			imp.outputRange.end += off;
		}
		imports.push(imp);
	}
	for (const u of inner.useBlocks) {
		u.startLine += off;
		u.endLine += off;
		if (u.configRange) {
			u.configRange.start += off;
			u.configRange.end += off;
		}
		if (u.inputsRange) {
			u.inputsRange.start += off;
			u.inputsRange.end += off;
		}
		if (u.outputRange) {
			u.outputRange.start += off;
			u.outputRange.end += off;
		}
		useBlocks.push(u);
	}
	for (const cd2 of inner.contentDefines) {
		cd2.startLine += off;
		cd2.endLine += off;
		if (cd2.inputsRange) {
			cd2.inputsRange.start += off;
			cd2.inputsRange.end += off;
		}
		if (cd2.outputRange) {
			cd2.outputRange.start += off;
			cd2.outputRange.end += off;
		}
		contentDefines.push(cd2);
	}
	for (const fb of inner.forBlocks) {
		fb.startLine += off;
		fb.endLine += off;
		if (fb.inputsRange) {
			fb.inputsRange.start += off;
			fb.inputsRange.end += off;
		}
		if (fb.outputRange) {
			fb.outputRange.start += off;
			fb.outputRange.end += off;
		}
		forBlocks.push(fb);
	}
	for (const line of inner.mdFencedLines) mdFencedLines.add(line + off);
	for (const block of inner.mdFencedBlocks) mdFencedBlocks.push({
		...block,
		startLine: block.startLine + off,
		endLine: block.endLine + off
	});
}
function maskLineRanges(innerLines, ranges, offset) {
	if (ranges.length === 0) return innerLines;
	const masked = [...innerLines];
	for (const range of ranges) {
		const start = Math.max(0, range.start - offset);
		const end = Math.min(masked.length - 1, range.end - offset);
		for (let i = start; i <= end; i++) masked[i] = "";
	}
	return masked;
}
/** Derive namespace from file path: ./shared/database.md → database */
function deriveNamespace(source) {
	return (source.split("/").pop() ?? source).replace(/\.md$/, "").replace(/[^a-zA-Z0-9_-]/g, "");
}
/**
* Scan raw text content for Magic MD syntax elements.
* Convenience wrapper that splits content into lines.
*/
function scanDocumentText(content) {
	return scanDocument(content.split(/\r?\n/));
}
/**
* Get all defined IDs from raw text.
*/
function getDefinitionIdsFromText(content) {
	return scanDocumentText(content).defines.map((d) => d.id);
}
//#endregion
export { applyTemplateInputRefsToString as C, substituteTemplateInputRefs as D, hasTemplateInputRefs as E, INPUT_REF_RE as S, extractTemplateParams as T, inferTemplateInputSchema as _, getDocumentAssetsDir as a, parseTemplateInputSchemaJson as b, isAssetRefPath as c, resolveDocumentAssetPath as d, TEMPLATE_INPUTS_FENCE_RE as f, getTemplateInputSchemaParams as g, getRequiredTemplateInputs as h, ASSETS_REF_PREFIX as i, normalizeAssetRefPath as l, getBlockingTemplateParams as m, scanDocument as n, getDocumentBundleDir as o, buildDefaultedTemplateInputs as p, scanDocumentText as r, getPathDirname as s, getDefinitionIdsFromText as t, parseAssetRefPath as u, mergeTemplateParams as v, applyTemplateInputRefsToValue as w, validateTemplateInputValues as x, normalizeTemplateInputSchema as y };
